import { useRef, useState } from "preact/hooks";
import { Logic, QuestionAnswer } from "@/types/questions";
import QuestionContainer from "@/components/QuestionContainer";
import SlideFooter from "@/components/SlideFooter";

interface BarRadioChartProps {
  onBack: (selectedAnswer: QuestionAnswer | null) => void;
  onNext: (selectedAnswer: QuestionAnswer) => void;
  questionText: string;
  answers: QuestionAnswer[];
  logic?: Logic;
  defaultSelectedAnswers: QuestionAnswer | null;
}

const BarRadioChart = ({
  onBack,
  onNext,
  questionText,
  answers,
  defaultSelectedAnswers,
}: BarRadioChartProps) => {
  const [selectedAnswer, setSelectedAnswer] = useState<QuestionAnswer | null>(
    defaultSelectedAnswers
  );
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleBack = () => {
    onBack(selectedAnswer);
  };

  const handleNext = () => {
    if (selectedAnswer) {
      onNext(selectedAnswer);
    }
  };

  const handleKeyDown = (e: KeyboardEvent, index: number) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      const nextIndex = (index + 1) % answers.length;
      buttonRefs.current[nextIndex]?.focus();
      setSelectedAnswer(answers[nextIndex]);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      const prevIndex = (index - 1 + answers.length) % answers.length;
      buttonRefs.current[prevIndex]?.focus();
      setSelectedAnswer(answers[prevIndex]);
    } else if (e.key === " ") {
      setSelectedAnswer(answers[index]);
    }
  };

  return (
    <>
      <QuestionContainer legendText={questionText} role="radiogroup">
        <div
          id="radiogroup"
          aria-label="Radio group"
          className="lc-flex lc-flex-col lg:lc-flex-row lc-gap-8 lc-justify-center"
        >
          {answers.map((answer, index) => (
            <button
              key={answer.id}
              id={answer.id}
              role="radio"
              tabIndex={
                index === 0 && !selectedAnswer
                  ? 0
                  : selectedAnswer?.id === answer.id
                  ? 0
                  : -1
              }
              type="button"
              ref={(el) => (buttonRefs.current[index] = el)}
              onClick={() => setSelectedAnswer(answer)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              aria-checked={selectedAnswer?.id === answer.id}
              className="lc-group lc-flex lc-flex-row lg:lc-flex-col lc-gap-3 lc-items-center lc-h-[60px] lg:lc-h-full lc-w-full lg:lc-w-[135px]"
            >
              <span className="lc-flex lc-flex-col lc-w-full lg:lc-h-[215px] lc-justify-end lc-items-start lg:lc-items-center">
                <span
                  className={`lc-flex lc-gap-4 lc-items-center lc-min-w-fit lc-p-4 lc-w-[${
                    answer.barSizeMobile ?? 0
                  }%] lc-h-[60px] lg:lc-w-28 lg:lc-h-[${answer.barSizeDesktop ?? 0}%] ${
                    selectedAnswer?.id === answer.id
                      ? "lc-bg-white-gradient"
                      : "lc-bg-blue-primary group-hover:md:lc-bg-blue-primary-hover"
                  } `}
                >
                  <span
                    className={`lg:lc-hidden lc-flex-shrink-0 lc-rounded-full lc-w-2 lc-h-2 lc-ring-[3px] ${
                      selectedAnswer?.id === answer.id
                        ? "lc-ring-cfa-blue-500"
                        : "lc-ring-white"
                    }`}
                  ></span>
                  <span
                    className={`lg:lc-hidden lc-font-azo-sans lc-max-w-36 lc-text-sm lc-text-left ${
                      selectedAnswer?.id === answer.id
                        ? "lc-text-cfa-blue-500"
                        : "lc-text-white "
                    }`}
                  >
                    {answer.text}
                  </span>
                </span>
              </span>
              <span
                className={`lc-flex-shrink-0 lc-hidden lg:lc-flex lc-rounded-full lc-w-2 lc-h-2 lc-ring-[3px] lc-ring-white ${
                  selectedAnswer?.id === answer.id ? "lc-bg-blue-500" : ""
                }`}
              ></span>
              <span className="lc-hidden lg:lc-flex lc-text-white lc-text-base lc-font-azo-sans">
                {answer.text}
              </span>
            </button>
          ))}
        </div>
      </QuestionContainer>
      <SlideFooter
        onNext={handleNext}
        onBack={handleBack}
        disableContinue={!selectedAnswer}
      />
    </>
  );
};

export default BarRadioChart;
